import { useCallback, useEffect, useRef, useState } from "react";
import {
  createAIWeeklyPlanDraft,
  createCycleFromWeeklyPlan,
  getOnboardingCycleConflicts,
  updateUserOnboarding,
} from "../../services/api";
import { createGenerationId } from "../generation/generationId";
import {
  GENERATION_RESULT_UNCERTAIN_MESSAGE,
  isGenerationTransportLoss,
} from "../generation/generationRecovery";
import { resolvePreferredTrainingDays } from "./trainingDayDefaults";
import useOnboardingGenerationProgress from "./useOnboardingGenerationProgress";

const TERMINAL_GENERATION_MESSAGES = Object.freeze({
  AI_GENERATION_ATTEMPTS_EXHAUSTED:
    "We couldn't finish your program after several tries. Try again to start a new generation.",
  AI_GENERATION_ABANDONED:
    "That program request expired before it could start. Try again to start a new generation.",
});

export const INITIAL_PROGRAM_FLOW = Object.freeze({
  phase: "step",
  generationId: null,
  generationStartedAt: null,
  window: null,
  conflicts: [],
  weeklyPlan: null,
  trainingDays: null,
  cycle: null,
  failedStage: null,
  completionDestination: "result",
  error: "",
});

// Re-exported from its promoted home so existing onboarding imports keep
// working; the implementation itself is now shared with the AI Builder.
export { createGenerationId };

export default function useOnboardingGeneration({
  applyOnboarding,
  draft,
  loadStatus,
  mountedRef,
  navigate,
  saveFinalTrainingProfile,
}) {
  const [programFlow, setProgramFlow] = useState(INITIAL_PROGRAM_FLOW);
  const generationInFlightRef = useRef(false);
  const generationRequestControllerRef = useRef(null);
  const pendingRecoveryRef = useRef(null);
  const recoveryDispatchStartedRef = useRef(false);
  const generationProgress = useOnboardingGenerationProgress(
    programFlow.phase,
    draft?.availability?.sessionsPerWeek,
    draft?.availability?.durationPerSession
  );

  const safeSetProgramFlow = useCallback((nextProgramFlow) => {
    if (mountedRef.current) {
      setProgramFlow(nextProgramFlow);
    }
  }, [mountedRef]);

  useEffect(() => () => {
    generationRequestControllerRef.current?.abort();
  }, []);

  useEffect(() => {
    if (
      programFlow.phase === "completing" &&
      generationProgress.completionReady
    ) {
      safeSetProgramFlow((current) => current.phase === "completing"
        ? { ...current, phase: "success" }
        : current);
    }
  }, [generationProgress.completionReady, programFlow.phase, safeSetProgramFlow]);

  async function runGenerationOperation(operation) {
    if (generationInFlightRef.current) {
      return;
    }
    generationInFlightRef.current = true;
    try {
      await operation();
    } finally {
      generationInFlightRef.current = false;
    }
  }

  async function completeOnboardingLifecycle(context, destination = "result") {
    safeSetProgramFlow({
      ...context,
      phase: "completing",
      failedStage: null,
      completionDestination: destination,
      error: "",
    });
    try {
      const lifecycleResponse = await updateUserOnboarding({
        action: "COMPLETE",
        trainingCycleId: context.cycle?.cycleId,
      });
      // Completion is the moment the bootstrap snapshot goes stale. Publishing
      // the authoritative response keeps OnboardingGate in agreement with the
      // server, so opening the generated cycle is not treated as an attempt to
      // reach a protected route before onboarding is done.
      applyOnboarding?.(lifecycleResponse?.onboarding);
      if (!mountedRef.current) {
        return;
      }
      if (destination === "home") {
        navigate("/", { replace: true });
        return;
      }
      generationProgress.markSuccess();
    } catch (error) {
      safeSetProgramFlow({
        ...context,
        phase: "error",
        failedStage: "completion",
        completionDestination: destination,
        error: error?.message || "Your program is ready, but onboarding could not be completed.",
      });
    }
  }

  async function convertWeeklyPlan(context) {
    const convertingContext = {
      ...context,
      phase: "converting",
      failedStage: null,
      error: "",
    };
    safeSetProgramFlow(convertingContext);
    try {
      const cycle = await createCycleFromWeeklyPlan({
        generationId: context.generationId,
        weeklyPlanParentId: context.weeklyPlan.weeklyPlanParentId,
        weeklyPlanVersionId: context.weeklyPlan.weeklyPlanVersionId,
        name: context.weeklyPlan.name,
        startDate: context.window.startDate,
        durationWeeks: 6,
        workoutDayAssignments: context.trainingDays.map((scheduledDay, index) => ({
          workoutOrderIndex: index + 1,
          scheduledDay,
        })),
        conflictWindow: context.window,
        confirmedConflicts: context.conflicts,
      });
      if (!mountedRef.current) {
        return;
      }
      generationProgress.markCycleReady();
      await completeOnboardingLifecycle({ ...context, cycle }, "result");
    } catch (error) {
      if (error?.code === "CYCLE_CONFLICT_CONFIRMATION_REQUIRED" && error?.details) {
        const confirmationContext = {
          ...context,
          phase: "confirmation",
          window: error.details.window,
          conflicts: error.details.conflicts || [],
          failedStage: null,
          error: "",
        };
        safeSetProgramFlow(confirmationContext);
        return;
      }
      if (error?.code === "AI_GENERATION_NOT_READY") {
        safeSetProgramFlow({
          ...context,
          phase: "error",
          failedStage: "ai",
          error: "Your saved program isn't ready to schedule yet. Try again to resume it.",
        });
        return;
      }
      safeSetProgramFlow({
        ...context,
        phase: "error",
        failedStage: "conversion",
        error: error?.message || "Your weekly plan was saved, but the training cycle could not be created.",
      });
    }
  }

  async function generateWeeklyPlan(context) {
    const generatingContext = {
      ...context,
      phase: "generating",
      failedStage: null,
      error: "",
    };
    safeSetProgramFlow(generatingContext);
    generationProgress.beginAI(context.generationId);
    try {
      let controller = new AbortController();
      generationRequestControllerRef.current = controller;
      let weeklyPlan;
      try {
        weeklyPlan = await createAIWeeklyPlanDraft({
          generationId: context.generationId,
          signal: controller.signal,
        });
      } catch (error) {
        const transportLoss = isGenerationTransportLoss(error);
        if (error?.code !== "AI_GENERATION_IN_PROGRESS" && !transportLoss) {
          throw error;
        }
        // Owned by the server already, or the response was lost after dispatch (proxy
        // timeout, dropped connection): recover through progress, never a new generation.
        const terminalStatus = await generationProgress.waitForAICompletion();
        if (terminalStatus === "UNAVAILABLE" && transportLoss) {
          throw new Error(GENERATION_RESULT_UNCERTAIN_MESSAGE);
        }
        if (terminalStatus !== "SUCCEEDED") {
          throw new Error(
            terminalStatus === "FAILED"
              ? "We couldn't generate your weekly plan."
              : "Program generation was interrupted."
          );
        }
        controller = new AbortController();
        generationRequestControllerRef.current = controller;
        weeklyPlan = await createAIWeeklyPlanDraft({
          generationId: context.generationId,
          signal: controller.signal,
        });
      }
      if (!mountedRef.current) {
        return;
      }
      if (generationRequestControllerRef.current === controller) {
        generationRequestControllerRef.current = null;
      }
      generationProgress.stopAI();
      generationProgress.markWeeklyPlanReady();
      await convertWeeklyPlan({ ...context, weeklyPlan });
    } catch (error) {
      generationRequestControllerRef.current = null;
      if (!mountedRef.current) {
        return;
      }
      generationProgress.stopAI();
      const terminalMessage = TERMINAL_GENERATION_MESSAGES[error?.code];
      const shouldMintOnRetry = terminalMessage && !context.serverOwned;
      const nextContext = shouldMintOnRetry
        ? {
            ...context,
            generationId: null,
            generationStartedAt: null,
          }
        : context;
      safeSetProgramFlow({
        ...nextContext,
        phase: "error",
        failedStage: shouldMintOnRetry
          ? "terminal-ai"
          : context.serverOwned ? "server-attempt" : "ai",
        error: terminalMessage || error?.message || "We couldn't generate your weekly plan.",
      });
    }
  }

  async function checkConflictsAndContinue(context) {
    const checkingContext = {
      ...context,
      phase: "checking",
      window: null,
      conflicts: [],
      failedStage: null,
      error: "",
    };
    safeSetProgramFlow(checkingContext);
    try {
      const preview = await getOnboardingCycleConflicts();
      if (!mountedRef.current) {
        return;
      }
      const nextContext = {
        ...checkingContext,
        window: preview.window,
        conflicts: preview.conflicts || [],
      };
      if (nextContext.conflicts.length > 0) {
        safeSetProgramFlow({ ...nextContext, phase: "confirmation" });
      } else {
        await generateWeeklyPlan(nextContext);
      }
    } catch (error) {
      safeSetProgramFlow({
        ...checkingContext,
        phase: "error",
        failedStage: "checking",
        error: error?.message || "We couldn't check your training cycle schedule.",
      });
    }
  }

  const restorePendingGeneration = useCallback((pendingGeneration, { trainingDays } = {}) => {
    if (!pendingGeneration?.generationId) {
      return;
    }
    const recoveredContext = {
      ...INITIAL_PROGRAM_FLOW,
      generationId: pendingGeneration.generationId,
      trainingDays,
      serverOwned: true,
    };

    if (pendingGeneration.status === "FAILED") {
      safeSetProgramFlow({
        ...recoveredContext,
        phase: "error",
        failedStage: "server-attempt",
        error: "We couldn't generate your weekly plan. Try again to resume the same request.",
      });
      return;
    }

    const hasCompletedCycle = pendingGeneration.status === "SUCCEEDED" &&
      pendingGeneration.trainingCycleId;
    pendingRecoveryRef.current = {
      ...recoveredContext,
      ...(hasCompletedCycle
        ? {
            recoveryPhase: "completion",
            cycle: { cycleId: pendingGeneration.trainingCycleId },
          }
        : { recoveryPhase: "checking" }),
    };
    recoveryDispatchStartedRef.current = false;
    safeSetProgramFlow({
      ...recoveredContext,
      phase: hasCompletedCycle
        ? "completing"
        : pendingGeneration.status === "RUNNING" ? "generating" : "checking",
    });
  }, [safeSetProgramFlow]);

  useEffect(() => {
    if (
      loadStatus !== "loaded" ||
      !pendingRecoveryRef.current ||
      recoveryDispatchStartedRef.current
    ) {
      return;
    }

    recoveryDispatchStartedRef.current = true;
    const recoveredContext = pendingRecoveryRef.current;
    pendingRecoveryRef.current = null;
    generationProgress.reset();
    runGenerationOperation(async () => {
      if (recoveredContext.recoveryPhase === "completion") {
        await completeOnboardingLifecycle(recoveredContext, "home");
        return;
      }
      await checkConflictsAndContinue(recoveredContext);
    });
    // Recovery is dispatched once from the state populated by initialize().
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadStatus]);

  async function beginProgramGeneration() {
    await runGenerationOperation(async () => {
      generationProgress.reset();
      const generationId = programFlow.generationId || createGenerationId();
      const generationStartedAt = programFlow.generationStartedAt || new Date().toISOString();
      safeSetProgramFlow({
        ...programFlow,
        generationId,
        generationStartedAt,
      });
      const settingsResponse = await saveFinalTrainingProfile();
      if (!mountedRef.current) {
        return;
      }
      const availability = settingsResponse?.trainingProfile?.profile?.availability || {};
      const trainingDays = resolvePreferredTrainingDays(
        availability.preferredTrainingDays,
        availability.sessionsPerWeek
      );
      await checkConflictsAndContinue({
        ...INITIAL_PROGRAM_FLOW,
        generationId,
        generationStartedAt,
        trainingDays,
      });
    });
  }

  async function handleConflictConfirm() {
    await runGenerationOperation(async () => {
      if (programFlow.weeklyPlan) {
        await convertWeeklyPlan(programFlow);
      } else {
        await generateWeeklyPlan(programFlow);
      }
    });
  }

  function handleConflictCancel() {
    const resumableContext = {
      ...programFlow,
      phase: "step",
      failedStage: null,
      error: "",
    };
    safeSetProgramFlow(resumableContext);
  }

  async function handleProgramRetry() {
    if (programFlow.failedStage === "terminal-ai") {
      await beginProgramGeneration();
      return;
    }
    await runGenerationOperation(async () => {
      if (programFlow.failedStage === "checking") {
        await checkConflictsAndContinue(programFlow);
      } else if (programFlow.failedStage === "server-attempt") {
        await checkConflictsAndContinue(programFlow);
      } else if (programFlow.failedStage === "ai") {
        await generateWeeklyPlan(programFlow);
      } else if (programFlow.failedStage === "conversion") {
        await convertWeeklyPlan(programFlow);
      } else if (programFlow.failedStage === "completion") {
        await completeOnboardingLifecycle(
          programFlow,
          programFlow.completionDestination || "result"
        );
      }
    });
  }

  return {
    beginProgramGeneration,
    generationProgress,
    handleConflictCancel,
    handleConflictConfirm,
    handleProgramRetry,
    programFlow,
    restorePendingGeneration,
  };
}
